import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react"
import { Annotation } from "../Managers/VisualizationManager/Variables/Annotations"
import {
    AnnotationReview,
    AnnotationReviews,
    EBOOSTQuestionID,
    FREQUENCY_OPTIONS,
    IICPatternType,
    PREVALENCE_OPTIONS,
} from "../Pages/Data/Visualize/DataReview/Types/EventReview"

const validPrevalenceValues = new Set<string>(PREVALENCE_OPTIONS)
const validFrequencyValues = new Set<string>(FREQUENCY_OPTIONS)

export type UseEventReviewFormStateParams = {
    annotationReviews: AnnotationReviews
    sortedAnnotations: Annotation[]
    currentAnnotationReview: AnnotationReview | undefined
}

export type UseEventReviewFormStateValue = {
    patternType: IICPatternType | null
    setPatternType: Dispatch<SetStateAction<IICPatternType | null>>
    location: string | null
    setLocation: Dispatch<SetStateAction<string | null>>
    prevalence: string | null
    setPrevalence: Dispatch<SetStateAction<string | null>>
    predominantFrequency: string | null
    setPredominantFrequency: Dispatch<SetStateAction<string | null>>
    maxFrequency: string | null
    setMaxFrequency: Dispatch<SetStateAction<string | null>>
    timeOfMaxFrequency: number | null
    setTimeOfMaxFrequency: Dispatch<SetStateAction<number | null>>
    isSetDateTimeActive: boolean
    setIsSetDateTimeActive: Dispatch<SetStateAction<boolean>>
    firstError: string | undefined
    errors: Map<string, string>
    updateValidationError: (id: string, error: string | undefined) => void
    resetValidation: () => void
    isComplete: boolean
    formIsEmpty: boolean
}

export function useEventReviewFormState({
    annotationReviews: _annotationReviews,
    sortedAnnotations: _sortedAnnotations,
    currentAnnotationReview,
}: UseEventReviewFormStateParams): UseEventReviewFormStateValue {
    const [patternType, setPatternType] = useState<IICPatternType | null>(null)
    const [location, setLocation] = useState<string | null>(null)
    const [prevalence, setPrevalence] = useState<string | null>(null)
    const [predominantFrequency, setPredominantFrequency] = useState<string | null>(null)
    const [maxFrequency, setMaxFrequency] = useState<string | null>(null)
    const [timeOfMaxFrequency, setTimeOfMaxFrequency] = useState<number | null>(null)
    const [isSetDateTimeActive, setIsSetDateTimeActive] = useState(false)
    const [errors, setErrors] = useState<Map<string, string>>(new Map())

    const updateValidationError = useCallback((id: string, error: string | undefined) => {
        setErrors(previous => {
            const updated = new Map(previous)
            if (error) {
                updated.set(id, error)
            } else {
                updated.delete(id)
            }
            return updated
        })
    }, [])

    const resetValidation = useCallback(() => setErrors(new Map()), [])

    const firstError = useMemo(() => {
        const messages = [...errors.values()]
        return messages.length > 0 ? messages[0] : undefined
    }, [errors])

    const isComplete = useMemo(() => {
        if (patternType === IICPatternType.OTHER) {
            return true
        }

        return (
            location !== null &&
            prevalence !== null &&
            predominantFrequency !== null &&
            maxFrequency !== null &&
            timeOfMaxFrequency !== null
        )
    }, [location, maxFrequency, patternType, predominantFrequency, prevalence, timeOfMaxFrequency])

    const formIsEmpty = (
        patternType === null &&
        location === null &&
        prevalence === null &&
        predominantFrequency === null &&
        maxFrequency === null &&
        timeOfMaxFrequency === null
    )

    const setExistingValues = useCallback((review: AnnotationReview) => {
        setPatternType(null)
        setLocation(null)
        setPrevalence(null)
        setPredominantFrequency(null)
        setMaxFrequency(null)
        setTimeOfMaxFrequency(null)

        review.form.forEach(questionResponse => {
            switch (questionResponse.id) {
                case EBOOSTQuestionID.IIC_PATTERN_TYPE:
                    setPatternType(questionResponse.response ?? null)
                    break
                case EBOOSTQuestionID.IIC_PATTERN_LOCATION:
                    setLocation(questionResponse.response ?? null)
                    break
                case EBOOSTQuestionID.IIC_PATTERN_PREVALENCE:
                    setPrevalence(validPrevalenceValues.has(questionResponse.response) ? questionResponse.response : null)
                    break
                case EBOOSTQuestionID.IIC_PREDOMINANT_FREQUENCY:
                    setPredominantFrequency(validFrequencyValues.has(questionResponse.response) ? questionResponse.response : null)
                    break
                case EBOOSTQuestionID.IIC_MAX_FREQUENCY:
                    setMaxFrequency(validFrequencyValues.has(questionResponse.response) ? questionResponse.response : null)
                    break
                case EBOOSTQuestionID.IIC_TIME_OF_MAX_FREQUENCY:
                    setTimeOfMaxFrequency(questionResponse.response ?? null)
                    break
                default:
                    break
            }
        })
    }, [])

    useEffect(() => {
        if (!currentAnnotationReview) {
            return
        }

        setExistingValues(currentAnnotationReview)
    }, [currentAnnotationReview, setExistingValues])

    useEffect(() => {
        if (patternType === IICPatternType.OTHER) {
            setLocation(null)
            setPrevalence(null)
            setPredominantFrequency(null)
            setMaxFrequency(null)
            setTimeOfMaxFrequency(null)
            resetValidation()
        }
    }, [patternType, resetValidation])

    return {
        patternType,
        setPatternType,
        location,
        setLocation,
        prevalence,
        setPrevalence,
        predominantFrequency,
        setPredominantFrequency,
        maxFrequency,
        setMaxFrequency,
        timeOfMaxFrequency,
        setTimeOfMaxFrequency,
        isSetDateTimeActive,
        setIsSetDateTimeActive,
        firstError,
        errors,
        updateValidationError,
        resetValidation,
        isComplete,
        formIsEmpty,
    }
}
